import React, { useState } from "react";

import isIntuit from "Intuit/isIntuit";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import { LabelSmall } from "Cobalt/BodyText";
import Button from "Cobalt/Button";
import { Title, TitleSmall } from "Cobalt/Header";
import { ReduxTriggerButton } from "Cobalt/Modal/ReduxTriggerButton";
import Stack from "Cobalt/Stack";
import BodyText from "Common/BodyText";
import { Card, CardSection } from "Common/Card";
import { Row, Col } from "Common/Grid";
import usePagination from "Common/Hooks/usePagination";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";
import { SizeSwitch } from "Common/Responsive";

import { FAQModal } from "./PopUpFAQs";
import { simplifiedQuestionsAndAnswers } from "./shared/FAQText";

import makeBem from "Utils/makeBem";

const ANALYTICS_LOCATION = "paginating_up_faq_page";
const bem = makeBem("PaginatingFAQs");

const PaginatingFAQs = () => {
  const [openKey, setOpenKey] = useState(null); // No panel is open initially

  const togglePanel = (key) => {
    setOpenKey((prevKey) => (prevKey === key ? null : key));
  };

  const questionsAndAnswersArr = simplifiedQuestionsAndAnswers();

  const { currentPage, totalPages, paginate, currentPageItems } = usePagination(questionsAndAnswersArr, 4);

  return (
    <SizeSwitch
      md={
        <Div pt={4}>
          <HeaderAndPagination currentPage={currentPage} totalPages={totalPages} paginate={paginate} />
          <Card theme="flat">
            {currentPageItems.map((FAQ, index) => {
              const { question, answer } = FAQ;
              return (
                <Div key={index} className="w-6/12 cursor-pointer" pt={3} pb={3} onClick={() => togglePanel(index)}>
                  <Stack direction="horizontal" justify="space-between" align="baseline">
                    <BodyText>{question}</BodyText>
                    <Icon icon="chevron-right" />
                  </Stack>
                  <FAQModal question={question} answer={answer} show={openKey === index} onHide={() => setOpenKey(null)} />
                </Div>
              );
            })}
          </Card>
        </Div>
      }
      xs={
        <>
          <Row>
            <Col>
              <Title p={4} pl={0}>
                How can we help
              </Title>
            </Col>
          </Row>
          <Div sm={12} className={bem("MobileQuestionContainer")}>
            {questionsAndAnswersArr.map((FAQ, index) => {
              const { question, answer } = FAQ;
              return (
                <Card key={index} mb={isIntuit() ? 3 : 4} className={bem("MobileToggleContainer")} mr={3}>
                  <CardSection
                    onClick={() => {
                      AnalyticsTracking.trackEvent("cta_clicked", {
                        location: ANALYTICS_LOCATION,
                        question,
                        action: openKey === index ? "close" : "open",
                      });

                      togglePanel(index);
                    }}
                    className={bem("QuestionContainer")}
                    p={0}
                  >
                    <Div className={bem("MobileToggle")} px={2}>
                      <BodyText>{question}</BodyText>
                      <Icon icon="chevron-right" pl={3} />
                    </Div>
                  </CardSection>
                  <FAQModal question={question} answer={answer} show={openKey === index} onHide={() => setOpenKey(null)} />
                </Card>
              );
            })}
          </Div>
        </>
      }
    />
  );
};

const HeaderAndPagination = ({ currentPage, totalPages, paginate }) => (
  <Div mb={3}>
    <Stack direction="horizontal" justify="space-between" align="baseline">
      <Stack direction="horizontal" gap="condensed" align="baseline">
        <TitleSmall mt={0} mb={0}>
          How Can We Help?
        </TitleSmall>
        <ReduxTriggerButton name="contactUsModal" color="link" pl={0}>
          Contact Us
        </ReduxTriggerButton>
      </Stack>
      <Div sm={3} className={bem("PaginationContainer")}>
        <LabelSmall mr={3}>
          {currentPage + 1} of {totalPages}
        </LabelSmall>
        <Button color="ghost" circle size="xs" onClick={() => paginate(-1)}>
          <Icon icon="chevron-left" />
        </Button>
        <Button color="ghost" circle size="xs" onClick={() => paginate(1)}>
          <Icon icon="chevron-right" data-testid="chevron-right" />
        </Button>
      </Div>
    </Stack>
  </Div>
);

export default PaginatingFAQs;
